import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { FC, useState } from 'react';
import styled, { css } from 'styled-components';

import BackButton from '@/components/Buttons/BackButton';
import DownloadButton from '@/components/Buttons/DownloadButton';
import ScrollToTop from '@/components/Buttons/ScrollToTop';
import ContainerStoryPage from '@/components/ContainerStoryPage/ContainerStoryPage';
import Footer from '@/components/Footer/Footer';
import HubspotForm from '@/components/Modal/HubspotForm';
import HubspotFormModal from '@/components/Modal/HubspotFormModal';
import Navigation from '@/components/Navigation/Navigation';
import RelevantStories from '@/components/RelevantStories/RelevantStories';
import Seo from '@/components/Seo/Seo';
import { HUBSPOT_FORM_IDS, LANGUAGES } from '@/lib/constants';
import { fetchAPI, getImage } from '@/lib/fetchAPI';
import { parseHTML } from '@/lib/parse';
import { translate } from '@/lib/translate';
import Partner from '@/modules/messaging-partners/pop-up/PartnerNew';

const ReactPlayer = dynamic(() => import('react-player/lazy'), { ssr: false });

const Story: FC<any> = ({ url, lang, data, relevantPosts }) => {
	const router = useRouter();
	const [messagingPartner, setMessagingPartner] = useState(false);
	const [currentFormId, setCurrentFormId] = useState<string | null>(null);

	const handlePartnerModalOpen = () => setMessagingPartner(true);

	const handlePartnerModalClose = () => setMessagingPartner(false);

	const handleBackButton = () => router.back();

	const openModal = (formId) => setCurrentFormId(formId);

	const closeModal = () => setCurrentFormId(null);

	return (
		<>
			<Seo
				url={`/success-stories/${url}`}
				postImage={data?.story?.preview?.url}
				description={data?.story?.shortDescription}
				title={translate(lang, {
					en: `Success stories | ${data?.story?.title}`,
					ru: `Истории успеха | ${data?.story?.title}`,
					ua: `Історії успіху | ${data?.story?.title}`,
				})}
			/>

			<Navigation lang={lang} />

			<Wrapper>
				{data?.fullscreenBanner ? (
					<BrandBanner $banner={data?.fullscreenBanner?.url} />
				) : null}

				<BackButtonContainer>
					<BackButton text="All stories" onClick={handleBackButton} />
				</BackButtonContainer>

				<ContainerStoryPage>
					{data?.brandLogo ? (
						<BrandIcon
							$banner={data?.fullscreenBanner?.url}
							src={getImage(data?.brandLogo)}
							alt={translate(lang, {
								en: `Viber Success Stories ${data?.url}`,
								ru: `Истории успеха Viber ${data?.url}`,
								ua: `Історії успіху Viber ${data?.url}`,
							})}
						/>
					) : null}

					<BrandName>{parseHTML(data?.story?.title)}</BrandName>

					<BrandDescription>
						{parseHTML(data?.story?.shortDescription)}
					</BrandDescription>

					<StatisticsRow>
						{data?.story?.statistics?.map((statistic, index) => (
							<Col key={`statistic_col_${index}`}>
								<StatisticDescription>
									{parseHTML(statistic.title)}
								</StatisticDescription>

								<StatisticNumber>{statistic.number}</StatisticNumber>
							</Col>
						))}
					</StatisticsRow>

					{data?.story?.aboutCompany?.title ? (
						<Content>
							<Left>{parseHTML(data?.aboutCompany.title)}</Left>

							<Right>{parseHTML(data?.aboutCompany.description)}</Right>
						</Content>
					) : null}

					{data?.story?.challenges?.map((challenge, index) => (
						<Content key={`challenge_${index}`}>
							<Left>{parseHTML(challenge.title)}</Left>

							<Right>{parseHTML(challenge.description)}</Right>
						</Content>
					))}

					{data?.story?.solutions ? (
						<>
							<Content>
								<Left>Solution</Left>

								<Right>{parseHTML(data?.story?.solutions?.description)}</Right>
							</Content>

							{data?.story?.solutions?.youtube ? (
								<PhoneContainer>
									<ReactPlayer
										loop
										muted
										playing
										playsinline
										width="100%"
										height="auto"
										style={{ aspectRatio: '791/482' }}
										url={data?.story?.solutions?.youtube}
									/>
								</PhoneContainer>
							) : data?.story?.solutions?.media?.url ? (
								<PhoneContainer>
									<PhonesImage
										loading="lazy"
										src={getImage(data?.story?.solutions?.media)}
										alt={translate(lang, {
											en: 'Viber Success Stories 1',
											ru: 'Истории успеха Viber 1',
											ua: 'Історії успіху Viber 1',
										})}
									/>
								</PhoneContainer>
							) : null}
						</>
					) : null}

					{data?.story?.results?.map((result, index) => (
						<Content key={`result_${index}`}>
							<Left>{parseHTML(result?.title)}</Left>

							<Right>{parseHTML(result?.description)}</Right>
						</Content>
					))}

					{data?.story?.quote ? (
						<Quote>
							<QuoteIcon src="/images/icons/quote-icon.svg" alt="Quote" />

							<QuoteContent>{data?.story?.quote?.text}</QuoteContent>

							<Author>
								{data?.story?.quote?.author?.authorImage ? (
									<AuthorImage
										loading="lazy"
										alt={data?.story?.quote?.author?.authorImage?.name}
										src={getImage(data?.story?.quote?.author?.authorImage)}
									/>
								) : null}

								<div>
									<Name>
										{parseHTML(data?.story?.quote?.author?.author?.name)}
									</Name>

									<Position>
										{data?.story?.quote?.author?.author?.position}
									</Position>
								</div>
							</Author>
						</Quote>
					) : null}

					{data?.story?.partner ? (
						<StoryPartner>
							<PartnerTitle>
								{parseHTML(
									data?.story?.partner?.richTitle ??
										data?.story?.partner?.title,
								)}
							</PartnerTitle>

							{data?.story?.partner?.partner?.logo ? (
								<PartnerLogo onClick={handlePartnerModalOpen}>
									<PartnerImage
										loading="lazy"
										src={getImage(data?.story?.partner?.partner?.logo)}
										alt={
											translate(lang, {
												en: 'Viber Partners',
												ru: 'Viber Партнеры',
												ua: 'Viber Партнери',
											}) +
											' ' +
											data?.partner?.url
										}
									/>
								</PartnerLogo>
							) : null}

							{messagingPartner ? (
								<Partner
									partner={data?.story?.partner?.partner}
									onModalClose={handlePartnerModalClose}
								/>
							) : null}
						</StoryPartner>
					) : null}

					{data?.story?.downloadCase?.showDownloadCase ? (
						<Download>
							<DownloadPreview>
								<img
									alt="download"
									src="/images/success-stories/cloud-download.png"
								/>

								<h2>{parseHTML(data?.story?.title)}</h2>

								<div>Success Story</div>
							</DownloadPreview>

							<DownloadButton
								text="Download full story"
								onClick={() => openModal(HUBSPOT_FORM_IDS.CASE_STUDY_MEDIAKIT)}
							/>
						</Download>
					) : null}

					<ScrollToTop />
				</ContainerStoryPage>

				<RelevantStories
					posts={relevantPosts}
					relevantTitle="You may also like"
				/>
			</Wrapper>

			<Footer lang={lang} />

			{currentFormId ? (
				<HubspotFormModal handleClose={closeModal}>
					<h2>Download your Case Study</h2>

					<div className="subtitleModal">
						From branded stickers and immersive ads to one-to-one,
						brand-to-consumer messaging. Learn more about our success stories
						and get inspired.
					</div>

					<HubspotForm formId={currentFormId} handleClose={closeModal} />
				</HubspotFormModal>
			) : null}
		</>
	);
};

const Wrapper = styled.div`
	margin-top: 81px;

	@media (max-width: 600px) {
		margin-top: 65px;
	}
`;

const BrandBanner = styled.div<{ $banner: string }>`
	display: block;
	width: 100%;
	border-bottom-left-radius: 80px;
	border-bottom-right-radius: 80px;
	background-image: url('${({ $banner }) => $banner}');
	height: 340px;
	background-size: cover;
	background-repeat: no-repeat;

	@media (max-width: 1175px) {
		height: 300px;
	}

	@media (max-width: 1024px) {
		height: 270px;
	}

	@media (max-width: 920px) {
		height: 240px;
	}

	@media (max-width: 830px) {
		height: 210px;
	}

	@media (max-width: 660px) {
		background-size: cover;
		background-repeat: no-repeat;
		height: 340px;
	}

	@media (max-width: 590px) {
		height: 270px;
	}

	@media (max-width: 440px) {
		height: 215px;
	}

	@media (max-width: 380px) {
		height: 195px;
	}
`;

const PhoneContainer = styled.div`
	text-align: center;
	margin: 80px 0;
`;

const PhonesImage = styled.img`
	width: 100%;
`;

const BrandIcon = styled.img<{ $banner?: boolean }>`
	width: 120px;
	display: block;
	margin: 0 auto;
	margin-bottom: 25px;
	margin-top: 20px;
	border-radius: 50px;
	box-shadow: 0px 19px 36px 5px rgba(40, 34, 83, 0.1);

	${({ $banner }) => css`
		margin-top: ${$banner ? '-100px' : '30px'};
	`}

	@media (max-width: 840px) {
		width: 106px;
		border-radius: 40px;
	}

	@media (max-width: 600px) {
		${({ $banner }) => css`
			margin-top: ${$banner ? '-60px' : '90px'};
		`}

		margin-bottom: 15px;
	}
`;

const BrandName = styled.h1`
	font-family: var(--font-sans);

	font-weight: var(--font-bold);
	font-size: 65px;
	line-height: 60px;
	text-align: center;
	color: var(--color-black);

	@media (max-width: 850px) {
		font-size: 55px;
	}

	@media (max-width: 600px) {
		font-size: 40px;
		line-height: 55px;
	}
`;

const BrandDescription = styled.div`
	font-family: var(--font-sans);

	font-size: 28px;
	line-height: 35px;
	color: var(--color-black);
	margin-top: 58px;
	margin-bottom: 77px;

	@media (max-width: 850px) {
		font-size: 23px;
	}

	@media (max-width: 600px) {
		font-size: 21px;
		line-height: 32px;
		margin-top: 20px;
		margin-bottom: 40px;
	}
`;

const StatisticsRow = styled.div`
	display: flex;
	align-items: stretch;
	justify-content: space-between;
	flex-wrap: wrap;
	margin-bottom: 60px;

	@media (max-width: 600px) {
		margin-bottom: 30px;
	}
`;

const Col = styled.div`
	width: 30%;
	display: flex;
	flex-direction: column;
	margin-bottom: 46px;

	@media (max-width: 600px) {
		width: 40%;
		margin-bottom: 25px;
	}
`;

const StatisticDescription = styled.div`
	font-family: var(--font-sans);

	font-weight: var(--font-medium);
	font-size: 14px;
	line-height: 20px;
	color: var(--color-purple-50);
	max-width: 140px;
	margin-bottom: 4px;
`;

const StatisticNumber = styled.h4`
	margin-top: auto;
	font-family: var(--font-sans);

	font-weight: var(--font-bold);
	font-size: 42px;
	line-height: 50px;
	color: var(--color-purple);

	@media (max-width: 600px) {
		font-size: 34px;
		line-height: 40px;
	}
`;

const Content = styled.div`
	display: flex;
	justify-content: space-between;
	margin-bottom: 44px;

	@media (max-width: 600px) {
		margin-bottom: 30px;
		flex-direction: column;
	}
`;

const Left = styled.div`
	font-family: var(--font-sans);

	font-size: 28px;
	line-height: 25px;
	color: var(--color-black);

	@media (max-width: 600px) {
		margin-bottom: 20px;
		font-size: 24px;
		line-height: 32px;
	}
`;

const Right = styled.div`
	width: 360px;
	max-width: 100%;
	font-family: var(--font-sans);

	font-size: 18px;
	line-height: 27px;
	color: var(--color-black);
	margin-top: 3px;

	@media (max-width: 600px) {
		font-size: 17px;
		line-height: 28px;
	}

	p {
		margin-top: 30px;
		margin-bottom: 30px;

		@media (max-width: 600px) {
			margin-top: 15px;
			margin-bottom: 15px;
		}

		&:nth-child(1) {
			margin-top: 0;
		}
	}

	ul,
	ol {
		width: 100%;
	}

	li {
		padding-left: 40px;
		margin-bottom: 20px;
		position: relative;

		&:after {
			content: '';
			position: absolute;
			top: 9px;
			left: 7px;
			width: 10px;
			height: 10px;
			background-image: url('/images/icons/heart-dot.svg');
			background-size: cover;
		}
	}
`;

const Quote = styled.div`
	position: relative;
	padding-left: 70px;
	padding-right: 20px;

	@media (max-width: 600px) {
		padding-left: 55px;
	}

	@media (max-width: 500px) {
		padding-left: 40px;
	}
`;

const QuoteIcon = styled.img`
	width: 44px;
	position: absolute;
	left: 0px;
	top: 6px;

	@media (max-width: 600px) {
		width: 30px;
	}

	@media (max-width: 500px) {
		width: 27px;
		left: -2px;
	}
`;

const QuoteContent = styled.div`
	font-family: var(--font-sans);

	font-size: 28px;
	line-height: 37px;
	color: var(--color-black);
	margin-bottom: 22px;

	@media (max-width: 600px) {
		font-size: 20px;
		line-height: 25px;
	}

	@media (max-width: 500px) {
		font-size: 17px;
		line-height: 28px;
	}

	@media (max-width: 350px) {
		font-size: 15px;
		line-height: 26px;
	}
`;

const Author = styled.div`
	display: flex;
	align-items: flex-start;
`;

const AuthorImage = styled.img`
	display: block;
	margin-right: 9px;
	border-radius: 17px;
	overflow: hidden;
	width: 50px;
`;

const Name = styled.div`
	font-family: var(--font-sans);

	font-weight: var(--font-medium);
	font-size: 14px;
	line-height: 20px;
	color: var(--color-black);
	margin-top: 2px;
`;

const Position = styled.div`
	font-family: var(--font-sans);

	font-weight: var(--font-medium);
	font-size: 14px;
	line-height: 20px;
	color: var(--color-purple-50);
	max-width: 300px;
`;

const Download = styled.div`
	width: 720px;
	margin: 0 auto;
	max-width: 100%;
	background-image: url('/images/success-stories/file-download-bg.png');
	background-size: cover;
	border-radius: 50px;
	padding-bottom: 50px;
	margin-top: 105px;
	text-align: center;
`;

const DownloadPreview = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 240px;
	height: 180px;
	max-width: 90%;
	margin: 0 auto;
	background-image: url('/images/success-stories/file-icon.png');
	background-size: cover;
	transform: translateY(-20px);

	img {
		display: block;
		width: 62px;
		margin-top: auto;
	}

	h2 {
		font-family: var(--font-sans);
		font-size: 18px;
		line-height: 25px;
		text-align: center;
		color: var(--color-black);
		margin-top: 8px;
		transform: translateY(3px);
	}

	p {
		font-family: var(--font-sans);

		font-size: 18px;
		line-height: 26px;
		text-align: center;
		color: var(--color-purple-50);
		margin-bottom: auto;
	}
`;

const BackButtonContainer = styled.div`
	display: flex;
	justify-content: flex-start;
	position: sticky;
	top: 80px;

	@media (max-width: 600px) {
		display: none;
	}

	& > button {
		margin-left: 60px;
		margin-top: 10px;
	}
`;

const StoryPartner = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-top: 80px;

	@media (max-width: 660px) {
		flex-direction: column;
		justify-content: center;
	}
`;

const PartnerLogo = styled.div`
	background-color: var(--color-white-30);
	border-radius: 24px;
	padding-left: 20px;
	padding-right: 20px;
	width: 45%;
	padding-top: 6px;
	padding-bottom: 6px;

	@media (max-width: 660px) {
		width: 60%;
	}

	@media (max-width: 500px) {
		width: 80%;
	}
`;

const PartnerImage = styled.img`
	width: 200px;
	max-width: 100%;
	display: block;
	margin: 0 auto;
`;

const PartnerTitle = styled.h2`
	font-family: var(--font-sans);
	font-size: 18px;
	line-height: 25px;
	color: var(--color-black);
	margin-right: 50px;
	width: 50%;
	max-width: 311px;

	@media (max-width: 660px) {
		margin: 0 auto;
		text-align: center;
		margin-bottom: 15px;
		width: auto;
	}
`;

export async function getStaticProps({ params }) {
	const { lang, url } = params;

	if (!url) return { notFound: true };

	const [currentStory, allStories] = await Promise.all([
		fetchAPI('/stories', {
			url,
			lang,
			populate: [
				'story',
				'story.statistics',
				'story.challenges',
				'story.results',
				'story.solutions',
				'story.downloadCase',
				'story.partner',
				'story.partner.partner',
				'story.quote',
				'story.quote.author',
				'story.quote.author.author',
				'story.quote.author.authorImage',
				'preview',
				'brandLogo',
				'fullscreenBanner',
				'industries',
				'products',
				'regions',
			],
		}),
		fetchAPI('/stories', { lang }),
	]);

	const data = currentStory?.data?.[0];

	if (!data) return { notFound: true };

	const relevantPosts = allStories?.data
		?.filter(({ url }) => data?.url !== url)
		?.slice(0, 3);

	// if (lang === 'ru' && story[0]?.data?.partner?.data?.partner?.url === 'wwf') return; // TODO: reimplement

	return {
		props: {
			url,
			lang,
			data,
			relevantPosts,
		},
	};
}

export async function getStaticPaths() {
	const entries = await fetchAPI('/stories');
	const paths: any = [];

	entries?.data?.forEach((entry) => {
		LANGUAGES.forEach((lang) => {
			paths.push({ params: { url: entry.url, lang: lang.code } });
		});
	});

	return {
		paths,
		fallback: false,
	};
}

export default Story;
